import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { roomsService } from '../services/rooms.service';
import { candidatsService } from '../services/candidats.service';
import { contactsService } from '../services/contacts.service';
import { statistiquesService } from '../services/statistiques.service';
import '../styles/CoupDOeil.css';

interface CoupDOeilProps {
  colocId: string;
}

interface Chiffre {
  cle: string;
  valeur: string;
  libelle: string;
  detail: string;
  route: string;
  alerte: boolean;
}

/**
 * Le coup d'œil du tableau de bord.
 *
 * L'écran d'accueil du propriétaire ne portait qu'une carte de publication —
 * et rien du tout une fois le site en ligne. Il ouvrait donc son back-office
 * sur une page vide, sans savoir s'il avait une candidature en attente ou une
 * chambre à relouer.
 *
 * Quatre chiffres, chacun cliquable vers l'écran qui permet d'agir.
 */
const CoupDOeil: React.FC<CoupDOeilProps> = ({ colocId }) => {
  const navigate = useNavigate();
  const [chiffres, setChiffres] = useState<Chiffre[] | null>(null);

  useEffect(() => {
    let abandonne = false;

    // Quatre sources indépendantes : une erreur sur l'une ne doit pas priver
    // le propriétaire des autres.
    Promise.all([
      roomsService.listForColoc(colocId).catch(() => null),
      candidatsService.listForColoc(colocId).catch(() => null),
      contactsService.listForColoc(colocId).catch(() => null),
      statistiquesService.get(colocId, 30).catch(() => null),
    ]).then(([chambres, candidats, contacts, audience]) => {
      if (abandonne) return;

      const resultat: Chiffre[] = [];

      if (chambres) {
        const libres = chambres.filter(c => c.status === 'available').length;
        const preavis = chambres.filter(c => c.status === 'notice').length;
        resultat.push({
          cle: 'chambres',
          valeur: `${libres}/${chambres.length}`,
          // Toujours au pluriel : la valeur est une fraction (« 0/3 »), et
          // « 0/3 chambre libre » se lit de travers.
          libelle: 'chambres libres',
          detail: preavis > 0
            ? `${preavis} en préavis, bientôt à relouer`
            : 'aucun préavis en cours',
          route: '/rooms',
          alerte: libres > 0,
        });
      }

      if (candidats) {
        const enAttente = candidats.filter(c => c.status === 'PENDING').length;
        resultat.push({
          cle: 'candidats',
          valeur: String(enAttente),
          libelle: enAttente > 1 ? 'candidatures en attente' : 'candidature en attente',
          detail: enAttente > 0 ? 'à étudier' : 'rien à traiter',
          route: '/candidats',
          alerte: enAttente > 0,
        });
      }

      if (contacts) {
        const nouveaux = contacts.filter(c => c.status === 'NEW').length;
        resultat.push({
          cle: 'contacts',
          valeur: String(nouveaux),
          libelle: nouveaux > 1 ? 'demandes sans réponse' : 'demande sans réponse',
          detail: nouveaux > 0 ? 'à assigner ou à traiter' : 'tout est suivi',
          route: '/contacts',
          alerte: nouveaux > 0,
        });
      }

      if (audience) {
        // Une colocation dont la mesure vient d'être activée affiche « 0 vue »
        // alors que rien n'est cassé. Un tiret le dit, un zéro le cacherait.
        const jamaisMesuree = audience.mesureDepuis === null;
        resultat.push({
          cle: 'audience',
          valeur: jamaisMesuree ? '—' : String(audience.vues.total),
          libelle: 'pages vues (30 jours)',
          detail: jamaisMesuree
            ? 'mesure tout juste activée'
            : `${audience.contacts} contact${audience.contacts > 1 ? 's' : ''} sur la période`,
          route: '/statistiques',
          // Alerte seulement quand l'audience existe SANS rien déclencher :
          // le site est vu, l'annonce ne convertit pas. En dessous de vingt
          // vues le signal ne voudrait rien dire.
          alerte: !jamaisMesuree && audience.vues.total >= 20 && audience.contacts === 0,
        });
      }

      setChiffres(resultat);
    });

    return () => { abandonne = true; };
  }, [colocId]);

  if (!chiffres || chiffres.length === 0) return null;

  return (
    <div className="coup-doeil">
      {chiffres.map(c => (
        <button
          key={c.cle}
          type="button"
          className={`coup-doeil-tuile ${c.alerte ? 'coup-doeil-tuile--alerte' : ''}`}
          onClick={() => navigate(c.route)}
        >
          <span className="coup-doeil-valeur">{c.valeur}</span>
          <span className="coup-doeil-libelle">{c.libelle}</span>
          <span className="coup-doeil-detail">{c.detail}</span>
        </button>
      ))}
    </div>
  );
};

export default CoupDOeil;
